import { Injectable, inject } from "@angular/core";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ValidationErrors,
} from "@angular/forms";
import { saveAs } from "file-saver";
import { BehaviorSubject, Observable } from "rxjs";
import { take } from "rxjs/operators";
import { ProductCategory } from "../master-data/master-data.model";
import { DatabaseService } from "../shared/database.service";
import { sortByStringWithEmptyAtEnd } from "../shared/helpers/helper-functions";
import {
  DescriptiveFormTemplate,
  DeviceAttachment,
  DeviceAttachmentForm,
  DeviceAttachmentGroup,
  DeviceAttachmentGroupForm,
  FormDocument,
  FormDocumentForm,
  FormTableType,
  FormTemplate,
  InteractiveAttachmentGroup,
  InteractiveAttachmentGroupForm,
  ServiceAttachment,
  ServiceAttachmentForm,
  ServiceAttachmentGroup,
  ServiceAttachmentGroupForm,
  ServiceAttachmentGroupType,
  ServiceDrawing,
  ServiceDrawingForm,
  ServiceSection,
  ServiceSectionForm,
  ServiceSignature,
  ServiceSignatureForm,
  ServiceTable,
  ServiceTableForm,
  TemplateFormForm,
} from "./forms.model";
import {
  ControlType,
  ServiceFormItem,
  ServiceItemForm,
} from "./item/item.model";
import {
  TableFieldType,
  TableItem,
  TableItemForm,
} from "./table-field/table-field.model";

@Injectable({
  providedIn: "root",
})
export class FormsService {
  private fb = inject(FormBuilder);
  private databaseService = inject(DatabaseService);

  private serviceFormsBS$: BehaviorSubject<DescriptiveFormTemplate[]> =
    new BehaviorSubject<DescriptiveFormTemplate[]>([]);
  loadedForms$: Observable<DescriptiveFormTemplate[]> =
    this.serviceFormsBS$.asObservable();

  private serviceFormBS$: BehaviorSubject<FormGroup | undefined> =
    new BehaviorSubject(
      new FormGroup(
        new TemplateFormForm(
          new FormTemplate("", [], [], [], [], [], [], [], []),
        ),
      ),
    );
  serviceForm$: Observable<FormGroup> = this.serviceFormBS$.asObservable();

  productCategories: ProductCategory[] = [];

  getAll() {
    this.databaseService
      .loadDescriptiveForms()
      .pipe(take(1))
      .subscribe((forms) => {
        this.serviceFormsBS$.next(
          sortByStringWithEmptyAtEnd(forms, (item) => item.title),
        );
        // this.serviceFormsBS$.next(
        //   forms.sort((a, b) => a.title.localeCompare(b.title)),
        // );
      });
  }

  addForm(form: FormTemplate) {
    return this.databaseService.addForm(form);
  }

  saveFormToFile(form: Map<string, unknown>) {
    const name = form.get("title").toString().replace(/\s/g, "") + ".txt";
    // flat the map to object
    const toObject = (map: Map<string, unknown>) => {
      const o = {};
      for (const [k, v] of map) {
        if (v instanceof Map) {
          o[k] = toObject(v);
        } else if (v instanceof Array) {
          const ar = [];
          v.forEach((e) =>
            e instanceof Map ? ar.push(toObject(e)) : ar.push(e),
          );
          o[k] = ar;
        } else {
          o[k] = v;
        }
      }
      return o;
    };
    const blob = new Blob([JSON.stringify(toObject(form))], {
      type: "text/plain;charset=utf-8",
    });
    saveAs(blob, name);
  }

  loadFormFromFile(text: string) {
    const data = JSON.parse(text);
    const formTempl = new FormTemplate(
      data.title,
      data.groups ?? [],
      data.fields ?? [],
      data.tables ?? [],
      data.signatures ?? [],
      data.attachments ?? [],
      data.attachmentGroups ?? [],
      data.interactiveAttachmentGroups ?? [],
      data.deviceAttachmentGroups ?? [],
      data.drawings ?? [],
      data.documents ?? [],
      data.uuid ?? null,
      data.active ?? true,
      data.formType ?? null,
      data.allObjects ?? false,
      data.signaturePerUser ?? false,
      data.multiFormAllowed ?? false,
      data.additionalServicemanAllowed ?? true,
      data.sendByEmail ?? false,
      data.attachmentOnly ?? false,
      data.permissions ?? null,
      data.isSurvey ?? false,
    );
    console.log("Loaded from JSON", data);
    const form = new FormGroup(new TemplateFormForm(formTempl));
    this.serviceFormBS$.next(form);
  }

  formInflate(index: number) {
    const forms = this.serviceFormsBS$.getValue();
    if (forms && forms.length > index) {
      const data = forms[index];
      this.databaseService.findFormByUuid(data.uuid).subscribe((result) => {
        const form = new FormGroup(new TemplateFormForm(result));
        this.serviceFormBS$.next(form);
      });
    }
  }

  setServiceForm(form) {
    this.serviceFormBS$.next(form);
  }

  formClear() {
    const copy = this.serviceFormBS$.getValue();
    copy.controls.title.setValue("");
    copy.controls.formType.setValue(null);
    const groups = copy.get("groups") as FormArray;
    groups.clear();
    const tables = copy.get("tables") as FormArray;
    tables.clear();
    const fields = copy.get("fields") as FormArray;
    fields.clear();
    const sign = copy.get("signatures") as FormArray;
    sign.clear();
    const drawings = copy.get("drawings") as FormArray;
    drawings.clear();
    const attachments = copy.get("attachments") as FormArray;
    attachments.clear();
    const attachmentGroups = copy.get("attachmentGroups") as FormArray;
    attachmentGroups.clear();
    const interactiveAttachmentGroups = copy.get(
      "interactiveAttachmentGroups",
    ) as FormArray;
    interactiveAttachmentGroups.clear();
    const deviceAttachmentGroups = copy.get(
      "deviceAttachmentGroups",
    ) as FormArray;
    deviceAttachmentGroups.clear();
    const documents = copy.get("documents") as FormArray;
    documents.clear();
    copy.controls.statuses.patchValue([]);
    copy.controls.allStatuses.setValue(true);
    copy.controls.permissions.setValue(null);
    copy.controls.isSurvey.setValue(false);
    this.serviceFormBS$.next(copy);
  }

  addSection() {
    const copy = this.serviceFormBS$.getValue();
    const array = copy.get("groups") as FormArray;
    array.push(
      this.fb.group(new ServiceSectionForm(new ServiceSection("", [], 0))),
    );
    this.serviceFormBS$.next(copy);
  }

  deleteSection(index: number) {
    const copy = this.serviceFormBS$.getValue();
    const array = copy.get("groups") as FormArray;
    array.removeAt(index);
    this.serviceFormBS$.next(copy);
  }

  addField() {
    const copy = this.serviceFormBS$.getValue();
    const array = copy.get("fields") as FormArray;
    array.push(
      this.fb.group(new ServiceItemForm(new ServiceFormItem("", null))),
    );
    this.serviceFormBS$.next(copy);
  }

  deleteField(field: number) {
    const copy = this.serviceFormBS$.getValue();
    const array = copy.get("fields") as FormArray;
    array.removeAt(field);
    this.serviceFormBS$.next(copy);
  }

  addTable(type: FormTableType) {
    const copy = this.serviceFormBS$.getValue();
    const array = copy.get("tables") as FormArray;

    array.push(this.fb.group(new ServiceTableForm(new ServiceTable("", type))));

    const newIndex = array.length - 1;
    if (type === FormTableType.DEVICES) {
      this.addTableColumn(newIndex, type);
    }
    const newGroup = array.at(newIndex);
    if (newGroup) {
      newGroup.setValidators(this.checkDuplicatedColsValidator);
      newGroup.updateValueAndValidity();
    }

    this.serviceFormBS$.next(copy);
  }

  checkDuplicatedColsValidator(
    control: AbstractControl,
  ): ValidationErrors | null {
    const columnsArray = control.get("columns") as FormArray;
    if (!columnsArray) {
      return null;
    }
    const colNames: string[] = [];
    columnsArray.controls.forEach((cl) => {
      if (cl.value?.columnKey) {
        colNames.push(cl.value.columnKey as string);
      }
    });

    const hasDuplicates = colNames.some(
      (element, index) => colNames.indexOf(element) !== index,
    );

    return hasDuplicates ? { duplicatedColumns: true } : null;
  }

  deleteTable(table: number) {
    const copy = this.serviceFormBS$.getValue();
    const array = copy.get("tables") as FormArray;
    array.removeAt(table);
    this.serviceFormBS$.next(copy);
  }

  addTableColumn(table: number, type: FormTableType) {
    const form = this.serviceFormBS$.getValue();
    const array = form.get("tables") as FormArray;
    const currentGroup = array.get(table.toString());
    const currentItems = currentGroup.get("columns") as FormArray;
    if (type === FormTableType.DEVICES) {
      const tableItems: TableItem[] = this.generateDeviceColumns();
      tableItems.map((ti) =>
        currentItems.push(this.fb.group(new TableItemForm(ti))),
      );
    } else {
      currentItems.push(
        this.fb.group(
          new TableItemForm(
            new TableItem(
              "",
              TableFieldType.TEXT,
              null,
              0,
              1,
              null,
              false,
              true,
            ),
          ),
        ),
      );
    }
  }

  generateDeviceColumns(): TableItem[] {
    const tItems: TableItem[] = [];
    tItems.push(
      new TableItem(
        "Uuid",
        TableFieldType.TEXT,
        "deviceUuid",
        0,
        1,
        null,
        false,
        false,
      ),
    );
    tItems.push(
      new TableItem(
        "Opis",
        TableFieldType.TEXT,
        "deviceRemarks",
        0,
        1,
        null,
        true,
        true,
      ),
    );
    tItems.push(
      new TableItem(
        "Kategoria",
        TableFieldType.DROPDOWN,
        "productCategoryUuid",
        0,
        1,
        null,
        true,
        true,
      ),
    );
    tItems.push(
      new TableItem(
        "Produkt",
        TableFieldType.DROPDOWN,
        "deviceProductUuid",
        0,
        1,
        null,
        true,
        true,
      ),
    );
    tItems.push(
      new TableItem(
        "Lokalizacja",
        TableFieldType.TEXT,
        "deviceLocation",
        0,
        1,
        null,
        true,
        true,
      ),
    );
    tItems.push(
      new TableItem(
        "Nr Seryjny",
        TableFieldType.TEXT,
        "deviceSerial",
        0,
        1,
        null,
        true,
        true,
      ),
    );
    tItems.push(
      new TableItem(
        "Instalator",
        TableFieldType.TEXT,
        "deviceFitter",
        0,
        1,
        null,
        true,
        true,
      ),
    );
    tItems.push(
      new TableItem(
        "Data Instalacji",
        TableFieldType.TEXT,
        "deviceInstallationDate",
        0,
        1,
        null,
        true,
        true,
      ),
    );
    return tItems;
  }

  deleteTableColumn(i: number, t: number) {
    const array = this.serviceFormBS$.getValue().get("tables") as FormArray;
    const currentGroup = array.get(t.toString());
    const currentItems = currentGroup.get("columns") as FormArray;
    currentItems.removeAt(i);
  }

  addItem(section: number) {
    const form = this.serviceFormBS$.getValue();
    const array = form.get("groups") as FormArray;
    const currentGroup = array.get(section.toString());
    const currentItems = currentGroup.get("fields") as FormArray;
    currentItems.push(
      this.fb.group(
        new ServiceItemForm(
          new ServiceFormItem("", ControlType.TEXT, "", "", true),
        ),
      ),
    );
  }

  deleteItem(i: number, s: number) {
    const array = this.serviceFormBS$.getValue().get("groups") as FormArray;
    const currentGroup = array.get(s.toString());
    const currentItems = currentGroup.get("fields") as FormArray;
    currentItems.removeAt(i);
  }

  addSignature() {
    const form = this.serviceFormBS$.getValue();
    const array = form.get("signatures") as FormArray;
    array.push(
      this.fb.group(new ServiceSignatureForm(new ServiceSignature(""))),
    );
    this.serviceFormBS$.next(form);
  }

  deleteSignature(signatureIndex: number) {
    const copy = this.serviceFormBS$.getValue();
    const array = copy.get("signatures") as FormArray;
    array.removeAt(signatureIndex);
    this.serviceFormBS$.next(copy);
  }

  addDrawing() {
    const form = this.serviceFormBS$.getValue();
    const array = form.get("drawings") as FormArray;
    array.push(
      this.fb.group(
        new ServiceDrawingForm(
          new ServiceDrawing("", "", true, null, false, 0),
        ),
      ),
    );
    this.serviceFormBS$.next(form);
  }

  deleteDrawing(drawingIndex: number) {
    const copy = this.serviceFormBS$.getValue();
    const array = copy.get("drawings") as FormArray;
    array.removeAt(drawingIndex);
    this.serviceFormBS$.next(copy);
  }

  addAttachment() {
    const form = this.serviceFormBS$.getValue();
    const array = form.get("attachments") as FormArray;
    array.push(
      this.fb.group(new ServiceAttachmentForm(new ServiceAttachment(""))),
    );
    this.serviceFormBS$.next(form);
  }

  deleteAttachment(attachmentIndex: number) {
    const copy = this.serviceFormBS$.getValue();
    const array = copy.get("attachments") as FormArray;
    array.removeAt(attachmentIndex);
    this.serviceFormBS$.next(copy);
  }

  addAttachmentGroup() {
    const form = this.serviceFormBS$.getValue();
    const array = form.get("attachmentGroups") as FormArray;
    array.push(
      this.fb.group(
        new ServiceAttachmentGroupForm(
          new ServiceAttachmentGroup(
            "",
            "",
            ServiceAttachmentGroupType.ALL_OPTIONAL,
          ),
        ),
      ),
    );
    this.serviceFormBS$.next(form);
  }

  deleteAttachmentGroup(attachmentGrIndex: number) {
    const copy = this.serviceFormBS$.getValue();
    const array = copy.get("attachmentGroups") as FormArray;
    array.removeAt(attachmentGrIndex);
    this.serviceFormBS$.next(copy);
  }

  addAttachmentToGroup(grIndex) {
    const form = this.serviceFormBS$.getValue();
    const array = form.get("attachmentGroups") as FormArray;
    const currentGroup = array.get(grIndex.toString());
    const currentAttachments = currentGroup.get("attachments") as FormArray;
    currentAttachments.push(
      this.fb.group(
        new ServiceAttachmentForm(
          new ServiceAttachment(
            "",
            "",
            "",
            false,
            true,
            false,
            currentAttachments.length + 1,
          ),
        ),
      ),
    );
    this.serviceFormBS$.next(form);
  }

  deleteAttachmentFromGroup(grIndex: number, attIndex: number) {
    const array = this.serviceFormBS$
      .getValue()
      .get("attachmentGroups") as FormArray;
    const currentGroup = array.get(grIndex.toString());
    const currentItems = currentGroup.get("attachments") as FormArray;
    currentItems.removeAt(attIndex);
  }

  // Interactive attachments
  addInteractiveAttachmentGroup() {
    const form = this.serviceFormBS$.getValue();
    const array = form.get("interactiveAttachmentGroups") as FormArray;
    array.push(
      this.fb.group(
        new InteractiveAttachmentGroupForm(
          new InteractiveAttachmentGroup(
            "",
            "",
            1,
            5,
            ServiceAttachmentGroupType.ALL_OPTIONAL,
          ),
        ),
      ),
    );
    this.serviceFormBS$.next(form);
  }

  deleteInteractiveAttachmentGroup(attachmentGrIndex: number) {
    const copy = this.serviceFormBS$.getValue();
    const array = copy.get("interactiveAttachmentGroups") as FormArray;
    array.removeAt(attachmentGrIndex);
    this.serviceFormBS$.next(copy);
  }

  addInteractiveAttachmentToGroup(grIndex) {
    const form = this.serviceFormBS$.getValue();
    const array = form.get("interactiveAttachmentGroups") as FormArray;
    const currentGroup = array.get(grIndex.toString());
    const currentAttachments = currentGroup.get("attachments") as FormArray;
    currentAttachments.push(
      this.fb.group(
        new ServiceAttachmentForm(
          new ServiceAttachment(
            "",
            "",
            "",
            false,
            true,
            false,
            currentAttachments.length + 1,
          ),
        ),
      ),
    );
    this.serviceFormBS$.next(form);
  }

  deleteInteractiveAttachmentFromGroup(grIndex: number) {
    const array = this.serviceFormBS$
      .getValue()
      .get("interactiveAttachmentGroups") as FormArray;
    const currentGroup = array.get(grIndex.toString());
    const currentItems = currentGroup.get("attachments") as FormArray;
    currentItems.removeAt(0);
  }

  addDeviceAttachmentGroup() {
    const form = this.serviceFormBS$.getValue();
    const array = form.get("deviceAttachmentGroups") as FormArray;
    array.push(
      this.fb.group(
        new DeviceAttachmentGroupForm(
          new DeviceAttachmentGroup(
            "",
            "",
            ServiceAttachmentGroupType.ALL_OPTIONAL,
          ),
        ),
      ),
    );
    this.serviceFormBS$.next(form);
  }

  deleteDeviceAttachmentGroup(attachmentGrIndex: number) {
    const copy = this.serviceFormBS$.getValue();
    const array = copy.get("deviceAttachmentGroups") as FormArray;
    array.removeAt(attachmentGrIndex);
    this.serviceFormBS$.next(copy);
  }

  addDeviceAttachmentToGroup(grIndex) {
    const form = this.serviceFormBS$.getValue();
    const array = form.get("deviceAttachmentGroups") as FormArray;
    const currentGroup = array.get(grIndex.toString());
    const currentAttachments = currentGroup.get("attachments") as FormArray;
    currentAttachments.push(
      this.fb.group(
        new DeviceAttachmentForm(
          new DeviceAttachment(
            "",
            "",
            "",
            "",
            false,
            true,
            false,
            currentAttachments.length + 1,
          ),
        ),
      ),
    );
    this.serviceFormBS$.next(form);
  }

  deleteDeviceAttachmentFromGroup(grIndex: number, attIndex: number) {
    const array = this.serviceFormBS$
      .getValue()
      .get("deviceAttachmentGroups") as FormArray;
    const currentGroup = array.get(grIndex.toString());
    const currentItems = currentGroup.get("attachments") as FormArray;
    currentItems.removeAt(attIndex);
  }

  addDocumentLink() {
    const form = this.serviceFormBS$.getValue();
    const array = form.get("documents") as FormArray;
    array.push(this.fb.group(new FormDocumentForm(new FormDocument(""))));
    this.serviceFormBS$.next(form);
  }

  deleteDocumentLink(dokLinkIndex: number) {
    const copy = this.serviceFormBS$.getValue();
    const array = copy.get("documents") as FormArray;
    array.removeAt(dokLinkIndex);
    this.serviceFormBS$.next(copy);
  }

  getFormValidationErrors(fg: FormGroup) {
    console.log(
      "%c ==>> Validation Errors: ",
      "color: red; font-weight: bold; font-size:15px;",
    );
    let totalErrors = 0;
    const fgErrors = fg.errors;
    if (fgErrors != null) {
      totalErrors++;
      console.log("Form Group errors", fgErrors);
    }
    Object.keys(fg.controls).forEach((key) => {
      const controlErrors: ValidationErrors = fg.get(key).errors;
      if (controlErrors != null) {
        totalErrors++;
        Object.keys(controlErrors).forEach((keyError) => {
          console.log(
            "Key control: " + key + ", keyError: " + keyError + ", err value: ",
            controlErrors[keyError],
          );
        });
      }
    });
    console.log("Number of errors: ", totalErrors);
  }

  hasFormValidationErrors(fg: FormGroup): boolean {
    let totalErrors = 0;
    const fgErrors = fg.errors;
    if (fgErrors != null) {
      totalErrors++;
    }
    Object.keys(fg.controls).forEach((key) => {
      const controlErrors: ValidationErrors = fg.get(key).errors;
      if (controlErrors != null) {
        totalErrors++;
      }
    });
    return totalErrors > 0;
  }
}
